import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import UpsellDetailModal from './UpsellDetailModal';
import { 
  getEffectiveVisitDate, 
  isToddlerRestrictedDate, 
  isToddlerSpecialUpsell 
} from '../../../../utils/visitDateUtils';
import { ticketNameQualifiesForCircusBundleDiscount } from '../../../../utils/circusBundleDiscount';
import {
  ticketRowIsCastleAndGardenEntry,
  ticketLineIsCastleAndGardenEntry,
  ticketRowIsGardenPlaygroundOnlyEntryBasket,
  castleAndGardenPaidQtyForFreeAnimalParkPromotion,
} from '../../../../utils/animalParkUpsell';

const isCastleGardenUpsellCardName = (name) => ticketLineIsCastleAndGardenEntry(name);

/** Upsell grid card for Garden & Playground only (mutually exclusive with Castle & Gardens) */
const isGardenPlaygroundUpsellCardName = (name) => {
  const n = (name || '').toLowerCase();
  if (ticketLineIsCastleAndGardenEntry(name)) return false;
  if (n.includes('castle') && (n.includes('garden') || n.includes('gardens'))) return false;
  return n.includes('garden') && (n.includes('playground') || n.includes('play ground'));
};

const FLOWER_GARDEN_PAGE_ID = '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
/** Pirate Takeover / BOOM Circus booking journey — themed Castle & Gardens upsell card */
const PIRATE_CIRCUS_PAGE_ID = '27d7f7cb-fd23-4c0c-bd71-8420c022ccf5';
const PONY_RIDES_UNLOCK_CODES = ['ADMISS00083', 'ADMISS00237'];
const ANIMAL_PARK_PAGE_IDS = [
  'b0f1a62a-a34a-4c4e-a88d-6fda03a19279',
  '03c1719d-b7ff-43b1-acd9-72c6fdd56f6a',
  '360f48e1-8680-4719-9d2e-fc87a84c974b', // May Day bank holiday castle entry (4 May 2026)
  'b17520e4-20c1-451c-84ea-38c476c5fad7',
];

const isWellnessDiscoveryFreeUpsell = (name) => {
  const n = String(name || '').toLowerCase();
  if (!n.includes('day of discovery')) return false;
  return n.includes('wellness') || n.includes('tree trail');
};

const isAviaryAfternoonTeaUpsellName = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('aviary tearoom') && n.includes('afternoon tea');
};

const isAfternoonTeaRequiredUpsellName = (name) => {
  const n = (name || '').toLowerCase();
  if (!n) return false;
  return (
    n.includes('afternoon tea') ||
    n.includes('aviary tearoom') ||
    (n.includes('festive') && n.includes('tea'))
  );
};

const isSantasGrottoCastleUpsellName = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('grotto') && n.includes('castle') && !n.includes('village') && !n.includes('retail');
};

const isHalloweenCraftsUpsellName = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('halloween crafts');
};

const HALLOWEEN_CRAFTS_DISPLAY_NAME =
  'Halloween Craft Sessions at The Belvoir Retail Village — Add £4.50';

/** Matches Gentleman's Afternoon Tea promo in UpsellDetailModal (21 June 2026 UK) */
const isJune212026VisitDate = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  return date.getFullYear() === 2026 && date.getMonth() === 5 && date.getDate() === 21;
};

const UpsellContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;
`;

const UpsellIntroPanel = styled.div`
  text-align: center;
  margin-bottom: clamp(1.25rem, 3vw, 1.75rem);
  padding: clamp(0.85rem, 2vw, 1.15rem) 0;
`;

const UpsellIntroAccent = styled.div`
  width: 40px;
  height: 3px;
  margin: 0 auto 0.9rem;
  border-radius: 99px;
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.accent},
    ${props => props.theme.colors.primary}
  );
  opacity: 0.85;
`;

const SectionEyebrow = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.42);
  margin-bottom: 0.45rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.45rem, 3.2vw, 1.85rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const SectionSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.75vw, 0.96rem);
  color: rgba(16, 29, 64, 0.52);
  margin: 0 auto;
  max-width: 34rem;
  line-height: 1.6;
`;

const upsellCardShadow = `
  0 1px 0 rgba(255, 255, 255, 0.92) inset,
  0 12px 36px rgba(16, 29, 64, 0.07)
`;

const UpsellGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(0.85rem, 2.5vw, 1.25rem);
  margin-bottom: 0.5rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const UpsellCard = styled.div`
  background: white;
  border: 1px solid
    ${props =>
      props.$isSelected ? 'rgba(157, 109, 73, 0.45)' : 'rgba(16, 29, 64, 0.08)'};
  border-radius: 16px;
  padding: clamp(1.15rem, 2.5vw, 1.5rem);
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
  position: relative;
  box-shadow: ${upsellCardShadow};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  
  &:hover {
    border-color: rgba(157, 109, 73, 0.35);
    transform: translateY(-2px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 18px 44px rgba(16, 29, 64, 0.1);
  }
  
  ${props => props.$isSelected && `
    border-color: rgba(157, 109, 73, 0.45);
    background: ${props.theme.colors.offWhite};
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 14px 40px rgba(16, 29, 64, 0.09);
  `}
  
  ${props => props.isSoldOut && `
    opacity: 0.7;
    background: #f8f9fa;
    border-color: #dee2e6;
    
    &:hover {
      transform: translateY(-2px);
    }
  `}

  ${props => props.$isLocked && `
    opacity: 0.5;
    background: #f8f9fa;
    border-color: #dee2e6;
    cursor: not-allowed;
    filter: grayscale(60%);

    &:hover {
      transform: none;
      box-shadow: ${upsellCardShadow};
      border-color: #dee2e6;
    }
  `}
`;

const UpsellCardExclusive = styled(UpsellCard)`
  position: relative;
  border: 1px solid rgba(157, 109, 73, 0.42);
  background: linear-gradient(165deg, #fffef9 0%, #ffffff 55%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 14px 40px rgba(157, 109, 73, 0.12);

  &:hover {
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.98) inset,
      0 20px 48px rgba(157, 109, 73, 0.14);
  }
`;

const UpsellCardFathersDay = styled(UpsellCard)`
  border: 1px solid rgba(201, 162, 39, 0.45);
  background: linear-gradient(135deg, #ffffff 0%, rgba(255, 251, 235, 0.97) 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 12px 32px rgba(201, 162, 39, 0.12);

  &:hover {
    border-color: rgba(180, 140, 30, 0.65);
  }
`;

const UpsellCardPirateCastle = styled(UpsellCard)`
  border: 1px solid rgba(101, 67, 33, 0.42);
  background: linear-gradient(168deg, #fffdf9 0%, #f6ece0 42%, #ebe2d5 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 14px 38px rgba(101, 67, 33, 0.12);

  &:hover {
    border-color: rgba(74, 48, 24, 0.58);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.98) inset,
      0 18px 44px rgba(101, 67, 33, 0.14);
  }
`;

const UpsellCardExplorer = styled(UpsellCard)`
  border: 2px solid rgba(46, 125, 50, 0.55);
  background: linear-gradient(165deg, #f3faf3 0%, #ffffff 48%, #eef7ee 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.98) inset,
    0 16px 44px rgba(46, 125, 50, 0.16);
  transform: translateY(-1px);

  &:hover {
    border-color: rgba(27, 94, 32, 0.7);
    transform: translateY(-3px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.98) inset,
      0 22px 52px rgba(46, 125, 50, 0.2);
  }

  ${props => props.$isSelected && `
    border-color: rgba(27, 94, 32, 0.75);
    background: linear-gradient(165deg, #e8f5e9 0%, #ffffff 55%);
  `}
`;

const UpsellCardWellness = styled(UpsellCard)`
  border: 1px solid rgba(178, 156, 141, 0.55);
  background: linear-gradient(165deg, #f7f3ee 0%, #ffffff 48%, #efe6dc 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 14px 40px rgba(48, 45, 44, 0.08);

  &:hover {
    border-color: rgba(178, 156, 141, 0.85);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.98) inset,
      0 20px 48px rgba(178, 156, 141, 0.22);
  }

  ${props => props.$isSelected && `
    border-color: rgba(48, 45, 44, 0.55);
    background: linear-gradient(165deg, #efe6dc 0%, #ffffff 55%);
  `}
`;

const WellnessImageStripe = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  background: linear-gradient(135deg, #302d2c 0%, #4a403c 42%, #b29c8d 100%);
  color: #f7f3ee;
  padding: 0.42rem 0.75rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: 14px 14px 0 0;
`;

const ExplorerImageStripe = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 42%, #43a047 58%, #1b5e20 100%);
  color: #ffffff;
  padding: 0.45rem 0.75rem;
  font-size: 0.7rem;
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  border-radius: 14px 14px 0 0;
  box-shadow: 0 2px 10px rgba(27, 94, 32, 0.35);
`;

const ExplorerPromoCopy = styled.div`
  background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
  color: #ffffff;
  padding: 0.55rem 0.85rem;
  border-radius: 8px;
  font-weight: 700;
  margin-bottom: 0.65rem;
  font-size: 0.88rem;
  line-height: 1.45;
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const PirateCastleImageStripe = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  background: linear-gradient(135deg, #3d2914 0%, #6b4423 38%, #8b5a2b 55%, #4a3020 100%);
  color: #fdf6e3;
  padding: 0.42rem 0.75rem;
  font-size: 0.65rem;
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 14px 14px 0 0;
  border-bottom: 1px solid rgba(212, 175, 55, 0.4);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
`;

const UpsellImageWrap = styled.div`
  position: relative;
  margin: calc(-1 * clamp(1.15rem, 2.5vw, 1.5rem)) calc(-1 * clamp(1.15rem, 2.5vw, 1.5rem)) 1.15rem;
  border-radius: 14px 14px 0 0;
  overflow: hidden;
`;

const FathersDayImageStripe = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  background: linear-gradient(135deg, #a67c00 0%, #d4af37 45%, #e8c547 55%, #c9a227 100%);
  color: #0f1f3a;
  padding: 0.4rem 0.75rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 14px 14px 0 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const UpsellImage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 10;
  min-height: 148px;
  max-height: 220px;
  background: ${props => props.imageUrl ? `url(${props.imageUrl})` : props.theme.colors.greyBackground};
  background-size: cover;
  background-position: ${props => props.$bgPosition || 'center'};
  border-radius: 0;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      rgba(16, 29, 64, 0) 35%,
      rgba(16, 29, 64, 0.35) 100%
    );
    pointer-events: none;
  }

  ${props =>
    !props.imageUrl &&
    `
    background: linear-gradient(165deg, rgba(241, 239, 234, 0.9) 0%, ${props.theme.colors.greyBackground} 100%);

    &::after {
      content: '🏰';
      font-size: clamp(2.25rem, 8vw, 3rem);
      opacity: 0.22;
      position: relative;
      z-index: 0;
    }
  `}
`;

const UpsellContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const UpsellName = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: clamp(1.05rem, 2.4vw, 1.22rem);
  font-weight: 700;
  margin: 0 0 0.4rem;
  line-height: 1.25;
`;

const UpsellSubtitle = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.84rem, 1.75vw, 0.92rem);
  color: rgba(16, 29, 64, 0.56);
  margin-bottom: 0.65rem;
  font-weight: 500;
  line-height: 1.45;
`;

const UpsellDescription = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.58);
  font-size: clamp(0.86rem, 1.75vw, 0.94rem);
  line-height: 1.55;
  margin: 0 0 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const UpsellPrice = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 2vw, 1rem);
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  margin: 0;
  padding: 0.28rem 0.6rem;
  background: rgba(157, 109, 73, 0.08);
  border-radius: 999px;
  border: 1px solid rgba(157, 109, 73, 0.18);
`;

const SoldOutBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(183, 28, 28, 0.94);
  color: white;
  padding: 0.38rem 0.75rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  z-index: 4;
  box-shadow: 0 4px 14px rgba(183, 28, 28, 0.35);
`;

const LockedBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(16, 29, 64, 0.82);
  color: white;
  padding: 0.38rem 0.75rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  z-index: 4;
  box-shadow: 0 4px 14px rgba(16, 29, 64, 0.2);
`;

const ChangeDateLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
`;

const QuantityButton = styled.button`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.offWhite};
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityInput = styled.input`
  width: 70px;
  text-align: center;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 0.75rem 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 0.65rem;
  right: 0.65rem;
  z-index: 4;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 999px;
  min-width: 26px;
  height: 26px;
  padding: 0 0.45rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(157, 109, 73, 0.35);
  border: 2px solid rgba(255, 255, 255, 0.95);
`;

const RequiredBanner = styled.div`
  background: linear-gradient(135deg, #1a3a52 0%, #2c5282 100%);
  color: white;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.4;

  span {
    font-size: 1.3rem;
    flex-shrink: 0;
  }
`;

const CircusBundleOffersBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  background: linear-gradient(135deg, #fdf6e3 0%, #f4e4bc 55%, #f0ddb8 100%);
  color: #1a3a52;
  border: 1px solid rgba(201, 162, 39, 0.5);
  padding: 1rem 1.2rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 0.92rem;
  line-height: 1.45;
  box-shadow: 0 2px 10px rgba(26, 58, 82, 0.06);

  .banner-icon {
    flex-shrink: 0;
    font-size: 1.35rem;
    line-height: 1;
    margin-top: 0.05rem;
  }

  .banner-title {
    display: block;
    font-size: 0.98rem;
    font-weight: 700;
    margin-bottom: 0.45rem;
    color: #142a44;
  }

  ul {
    margin: 0;
    padding-left: 1.2rem;
  }

  li {
    margin-bottom: 0.35rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const RequiredBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%);
  color: white;
  padding: 0.38rem 0.75rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  z-index: 4;
  box-shadow: 0 4px 14px rgba(211, 47, 47, 0.35);
`;

const MaxQtyNote = styled.div`
  font-size: 0.78rem;
  color: rgba(16, 29, 64, 0.44);
  margin-top: 0.3rem;
  text-align: right;
`;

const UpsellCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  margin-top: auto;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(16, 29, 64, 0.06);
`;

const UpsellCardAction = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  border: 1px solid ${props => props.$selected ? 'rgba(157, 109, 73, 0.38)' : 'rgba(16, 29, 64, 0.13)'};
  background: ${props => props.$selected ? 'rgba(157, 109, 73, 0.09)' : 'rgba(16, 29, 64, 0.03)'};
  color: ${props => props.$selected ? props.theme.colors.accent : 'rgba(16, 29, 64, 0.48)'};
  transition: background 0.18s ease, border-color 0.18s ease;
`;

const UpsellCardStatusNote = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
  flex: 1;
  color: ${props => props.$variant === 'danger' ? 'rgba(183, 28, 28, 0.78)' : 'rgba(16, 29, 64, 0.44)'};
`;

const UpsellFreeTag = styled.div`
  display: inline-flex;
  align-items: baseline;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.92rem;
  font-weight: 700;
  color: #2e7d32;
  padding: 0.28rem 0.6rem;
  background: rgba(46, 125, 50, 0.08);
  border-radius: 999px;
  border: 1px solid rgba(46, 125, 50, 0.2);
`;

const WellnessFreeTag = styled(UpsellFreeTag)`
  color: #302d2c;
  background: rgba(215, 183, 167, 0.35);
  border-color: rgba(178, 156, 141, 0.45);
`;

const UpsellPricePer = styled.span`
  font-size: 0.72rem;
  font-weight: 500;
  opacity: 0.7;
  margin-left: 0.1rem;
`;

const MerchandiseQtyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid rgba(157, 109, 73, 0.35);
  background: rgba(157, 109, 73, 0.06);
`;

const MerchandiseQtyBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  transition: background 0.15s ease;
  flex-shrink: 0;
  &:hover { background: rgba(157, 109, 73, 0.12); }
  &:active { background: rgba(157, 109, 73, 0.2); }
`;

const MerchandiseQtyCount = styled.span`
  min-width: 1.6rem;
  text-align: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  border-left: 1px solid rgba(157, 109, 73, 0.2);
  border-right: 1px solid rgba(157, 109, 73, 0.2);
  padding: 0 0.2rem;
  line-height: 2rem;
`;

const MerchandiseAddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(157, 109, 73, 0.35);
  background: rgba(157, 109, 73, 0.06);
  color: ${props => props.theme.colors.accent};
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;
  &:hover { background: rgba(157, 109, 73, 0.13); border-color: rgba(157, 109, 73, 0.5); }
`;

const MerchandiseAddBtnStrong = styled(MerchandiseAddBtn)`
  border-color: rgba(46, 125, 50, 0.45);
  background: linear-gradient(135deg, #2e7d32 0%, #43a047 100%);
  color: #ffffff;
  font-weight: 700;
  padding: 0.45rem 0.95rem;
  box-shadow: 0 4px 14px rgba(46, 125, 50, 0.28);

  &:hover {
    background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
    border-color: rgba(27, 94, 32, 0.65);
    color: #ffffff;
  }
`;

const EmptyStateCard = styled.div`
  text-align: center;
  padding: clamp(2rem, 5vw, 2.75rem) clamp(1.25rem, 4vw, 2rem);
  background: linear-gradient(
    165deg,
    rgba(241, 239, 234, 0.65) 0%,
    rgba(255, 255, 255, 0.95) 100%
  );
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 16px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 12px 36px rgba(16, 29, 64, 0.06);
`;

const NoUpsells = styled(EmptyStateCard)`
  color: rgba(16, 29, 64, 0.62);

  p {
    font-family: ${props => props.theme.fonts.main};
    font-size: clamp(1.05rem, 2.5vw, 1.2rem);
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 0.5rem;
    line-height: 1.35;
  }

  small {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: clamp(0.88rem, 1.85vw, 0.95rem);
    line-height: 1.55;
    display: block;
    max-width: 26rem;
    margin: 0 auto;
    opacity: 0.88;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.65rem;
  padding: clamp(2rem, 5vw, 3rem) 1.5rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 2vw, 1rem);
  color: rgba(16, 29, 64, 0.55);

  &::before {
    content: '';
    width: 22px;
    height: 22px;
    border: 2px solid rgba(16, 29, 64, 0.12);
    border-top-color: ${props => props.theme.colors.accent};
    border-radius: 50%;
    animation: upsellSpin 0.85s linear infinite;
  }

  @keyframes upsellSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const CircusDiscountActiveBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  text-align: center;
  padding: 0.85rem 1.1rem;
  margin-bottom: 1rem;
  border-radius: 12px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.86rem, 1.85vw, 0.93rem);
  font-weight: 700;
  color: #1b5e20;
  background: linear-gradient(
    165deg,
    rgba(232, 245, 233, 0.95) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
  border: 1px solid rgba(46, 125, 50, 0.28);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 8px 24px rgba(16, 29, 64, 0.05);
`;

const FEATURED_TOUR_UPSELL_MAP = {
  'de28c2dc-c033-4de1-a18b-615b3c224cfa': { pattern: 'crown tour', displayName: 'The Crown Tour' },
  '40c6aa85-2a28-49ee-b884-8839c71d3f4b': { pattern: 'butler saw', displayName: 'What the Butler Saw Tour' },
};

const formatDateLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const UpsellStep = ({ 
  upsells = [], 
  selectedUpsells = [], 
  onUpsellSelection,
  onUpsellItemAdd,
  onUpsellRemove,
  selectedDate,
  selectedTimeSlot,
  selectedTickets = [],
  locationId,
  pageId,
  isFeaturedTour = false,
  requireAfternoonTea = false,
  loading = false,
  onChangeDateClick, // New prop for going back to date selection
  openAnimalParkUpsellNonce = 0, // Incremented by parent to programmatically open Animal Park modal
  openCircusUpsellNonce = 0 // Incremented by parent to programmatically open Circus upsell modal
}) => {
  const [localSelections, setLocalSelections] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUpsell, setSelectedUpsell] = useState(null);
  const lastHandledAnimalParkNonceRef = useRef(0);
  const lastHandledCircusNonceRef = useRef(0);

  // Removed: Santa's Grotto sold-out message logic - no longer needed

  const visitDate = useMemo(
    () => getEffectiveVisitDate(selectedDate, selectedTimeSlot, selectedTickets),
    [selectedDate, selectedTimeSlot, selectedTickets]
  );

  // Check if user has circus tickets (main ticket OR upsell) — any circus show line except workshops
  const hasCircusTickets = useMemo(() => {
    return selectedTickets.some(t => {
      const qty = parseInt(t.quantity, 10) || 0;
      if (qty <= 0) return false;
      return ticketNameQualifiesForCircusBundleDiscount(t.name);
    });
  }, [selectedTickets]);

  // Only show "Discount now Active" when there are entry tickets that actually get the 20% off
  const hasEligibleEntryForCircusDiscount = useMemo(() => {
    if (!hasCircusTickets) return false;
    return selectedTickets.some(t => {
      const name = (t.name || '').toLowerCase();
      const price = parseFloat(t.price) || 0;
      const quantity = parseInt(t.quantity, 10) || 0;
      if (price <= 0 || quantity <= 0) return false;
      if (name.includes('afternoon tea')) return false;
      if (name.includes('circus') || name.includes('workshop')) return false;
      return true; // entry-type ticket that qualifies for 20% off
    });
  }, [selectedTickets, hasCircusTickets]);

  // Main step: user chose an Animal Park ticket type
  const hasAnimalParkMainTicket = useMemo(() => {
    if (!ANIMAL_PARK_PAGE_IDS.includes(pageId)) return false;
    const mainTickets = selectedTickets.filter(
      t => !t.isUpsellItem && t.pageType !== 'EventTemplate'
    );
    return mainTickets.some(
      t => t.quantity > 0 && t.name && String(t.name).toLowerCase().includes('animal park')
    );
  }, [pageId, selectedTickets]);

  // Any journey (e.g. Pirate/Circus + castle upsell): castle lines in basket unlock free Animal Park upsell
  const hasCastleAndGardenEntryInBasket = useMemo(() => {
    return selectedTickets.some(t => {
      const qty = parseInt(t.quantity, 10) || 0;
      if (qty <= 0) return false;
      return ticketRowIsCastleAndGardenEntry(t);
    });
  }, [selectedTickets]);

  const hasGardenPlaygroundOnlyEntryInBasket = useMemo(() => {
    return selectedTickets.some(t => ticketRowIsGardenPlaygroundOnlyEntryBasket(t));
  }, [selectedTickets]);

  const paidCastleQtyForFreeAnimalParkPromo = useMemo(
    () => castleAndGardenPaidQtyForFreeAnimalParkPromotion(selectedTickets),
    [selectedTickets]
  );

  const exclusiveAnimalParkFreeUpsellsOnly = useMemo(
    () => hasAnimalParkMainTicket || paidCastleQtyForFreeAnimalParkPromo > 0,
    [hasAnimalParkMainTicket, paidCastleQtyForFreeAnimalParkPromo]
  );

  // Pony Rides requires any Animal Park ticket in the basket
  const hasAnimalParkTicketForPonyRides = useMemo(() => {
    return selectedTickets.some(t => {
      const qty = parseInt(t.quantity, 10) || 0;
      if (qty <= 0) return false;
      if (PONY_RIDES_UNLOCK_CODES.includes(t.productCode)) return true;
      // Upsell items don't carry productCode — match by name instead
      const name = (t.name || t.parentUpsellName || '').toLowerCase();
      if (name.includes('animal park')) return true;
      return false;
    });
  }, [selectedTickets]);

  // On non-Animal Park pages: hide Animal Park-specific upsells
  const isAnimalParkUpsellToHide = (name) => {
    const n = (name || '').toLowerCase();
    return (
      (n.includes('animal park free') && n.includes('promotion')) ||
      n.includes('animal park only')
    );
  };

  // Exclude Castle and Garden Entry + Garden and Playground (Only) Entry from upsell cards
  const isExcludedUpsell = (name) => {
    const n = (name || '').toLowerCase().trim();
    return (
      n.includes('castle and garden entry - adult - entry') ||
      n.includes('castle and garden entry - child - entry') ||
      n.includes('castle and garden entry - concession - entry') ||
      n.includes('garden and playground only entry - adult - entry') ||
      n.includes('garden and playground only entry - child - entry') ||
      n.includes('garden and playground entry - concession - entry') ||
      n.includes('garden and playground only entry - concession - entry') ||
      n.includes('garden and playground only entry - concession')
    );
  };

  const filteredUpsells = useMemo(() => {
    let result = upsells;

    // Toddler restriction: hide toddler-specific upsells on restricted dates
    if (visitDate && isToddlerRestrictedDate(visitDate)) {
      result = result.filter(upsell => !isToddlerSpecialUpsell(upsell.name));
    }

    // Exclude Castle and Garden Entry + Garden and Playground (Only) Entry
    result = result.filter(upsell => !isExcludedUpsell(upsell.name));

    // Do not replace the whole grid when castle unlocks free Animal Park — only toggle styling/modal logic per card below.
    if (!ANIMAL_PARK_PAGE_IDS.includes(pageId)) {
      result = result.filter(upsell => {
        const n = (upsell.name || '').toLowerCase();
        // Castle or Garden & Playground in basket: keep Animal Park upsell cards (otherwise some SKU titles stay hidden by isAnimalParkUpsellToHide)
        if (paidCastleQtyForFreeAnimalParkPromo > 0 && n.includes('animal park') && !n.includes('animal park only')) {
          return true;
        }
        return !isAnimalParkUpsellToHide(upsell.name);
      });
    }

    // Push Explorer lunch packs to the front so the online-save offer is hard to miss
    result = [...result].sort((a, b) => {
      if (requireAfternoonTea) {
        const aTea = isAfternoonTeaRequiredUpsellName(a.name) ? 0 : 1;
        const bTea = isAfternoonTeaRequiredUpsellName(b.name) ? 0 : 1;
        if (aTea !== bTea) return aTea - bTea;
      }
      const aMerch = a.pageType === 'Merchandise' ? 0 : 1;
      const bMerch = b.pageType === 'Merchandise' ? 0 : 1;
      return aMerch - bMerch;
    });

    return result;
  }, [upsells, visitDate, pageId, hasCastleAndGardenEntryInBasket, requireAfternoonTea]);


  // Initialize local selections from props
  useEffect(() => {
    const initialSelections = {};
    selectedUpsells.forEach(upsell => {
      initialSelections[upsell.id] = upsell.quantity || 0;
    });
    setLocalSelections(initialSelections);
  }, [selectedUpsells]);

  // Total non-upsell entry tickets in the basket (used for featured tour quantity cap)
  const totalEntryTickets = useMemo(() => {
    return selectedTickets
      .filter(t => !t.isUpsellItem && t.pageType !== 'EventTemplate' && (t.quantity || 0) > 0)
      .reduce((sum, t) => sum + (parseInt(t.quantity, 10) || 0), 0);
  }, [selectedTickets]);

  const tourConfig = isFeaturedTour ? FEATURED_TOUR_UPSELL_MAP[pageId] : null;
  const isFeaturedTourUpsell = (name) =>
    tourConfig ? (name || '').toLowerCase().includes(tourConfig.pattern) : false;

  const handleQuantityChange = (upsellId, quantity) => {
    const upsellItem = upsells.find(u => u.id === upsellId);
    const maxQty = (
      (isFeaturedTour && upsellItem && isFeaturedTourUpsell(upsellItem.name)) ||
      (requireAfternoonTea && upsellItem && isAfternoonTeaRequiredUpsellName(upsellItem.name))
    )
      ? totalEntryTickets
      : 10;
    const newQuantity = Math.max(0, Math.min(quantity, maxQty));
    
    setLocalSelections(prev => ({
      ...prev,
      [upsellId]: newQuantity
    }));

    if (upsellItem) {
      onUpsellSelection(upsellItem, newQuantity);
    }
  };

  const incrementQuantity = (upsellId) => {
    const currentQuantity = localSelections[upsellId] || 0;
    const upsellItem = upsells.find(u => u.id === upsellId);
    const maxQty = (
      (isFeaturedTour && upsellItem && isFeaturedTourUpsell(upsellItem.name)) ||
      (requireAfternoonTea && upsellItem && isAfternoonTeaRequiredUpsellName(upsellItem.name))
    )
      ? totalEntryTickets
      : 10;
    if (currentQuantity >= maxQty) return;
    handleQuantityChange(upsellId, currentQuantity + 1);
  };

  const decrementQuantity = (upsellId) => {
    const currentQuantity = localSelections[upsellId] || 0;
    handleQuantityChange(upsellId, currentQuantity - 1);
  };

  const handleQuantityInputChange = (upsellId, value) => {
    const quantity = parseInt(value) || 0;
    handleQuantityChange(upsellId, quantity);
  };

  const handleUpsellClick = (upsell, isSoldOut, isLocked) => {
    if (isSoldOut || isLocked) return;
    // Merchandise items are handled inline — no modal
    if (upsell.pageType === 'Merchandise') return;
    setSelectedUpsell(upsell);
    setModalOpen(true);
  };

  const buildMerchandiseItems = (upsell, qty) => {
    const selectedDateStr = selectedDate
      ? `${formatDateLocal(selectedDate)}T${selectedTimeSlot?.time || '10:00'}:00`
      : null;
    return Array.from({ length: qty }, () => ({
      webSaleItemId: upsell.id,
      webSaleItemTypeID: upsell.webSaleItemTypeID || 16,
      name: upsell.name || '',
      description: upsell.description || '',
      price: upsell.price ?? 0,
      maxPurchaseQty: upsell.maxPurchaseQty ?? 999,
      quantity: 1,
      pageType: 'Merchandise',
      pageId: pageId,
      eventId: null,
      eventCapacityUsageId: null,
      selectedDate: selectedDateStr,
      selectedEventTime: null,
      parentWebSaleItemId: upsell.parentWebSaleItemId || null,
      isUpsellItem: true,
      parentUpsellId: upsell.id,
      parentUpsellName: upsell.name,
    }));
  };

  const handleMerchandiseAdd = (e, upsell) => {
    e.stopPropagation();
    const currentQty = localSelections[upsell.id] || 0;
    const maxQty = upsell.maxPurchaseQty ?? 999;
    if (currentQty >= maxQty) return;
    const newQty = currentQty + 1;
    setLocalSelections(prev => ({ ...prev, [upsell.id]: newQty }));
    if (onUpsellItemAdd) {
      onUpsellItemAdd(buildMerchandiseItems(upsell, newQty));
    }
  };

  const handleMerchandiseDecrement = (e, upsell) => {
    e.stopPropagation();
    const currentQty = localSelections[upsell.id] || 0;
    if (currentQty <= 0) return;
    const newQty = currentQty - 1;
    setLocalSelections(prev => ({ ...prev, [upsell.id]: newQty }));
    if (newQty === 0) {
      if (onUpsellRemove) onUpsellRemove(upsell.id);
    } else {
      if (onUpsellItemAdd) {
        onUpsellItemAdd(buildMerchandiseItems(upsell, newQty));
      }
    }
  };

  // Parent (reminder modal "Stay — add Animal Park") bumps nonce → open Animal Park upsell modal
  useEffect(() => {
    if (!openAnimalParkUpsellNonce || loading) return;
    if (openAnimalParkUpsellNonce === lastHandledAnimalParkNonceRef.current) return;

    const ap = filteredUpsells.find(u => {
      const n = (u.name || '').toLowerCase();
      return n.includes('animal park') && !n.includes('animal park only');
    });
    if (!ap) return;

    lastHandledAnimalParkNonceRef.current = openAnimalParkUpsellNonce;

    const isSoldOut = ap.quantityAvailable === 0 || ap.isSoldOut === true;
    const nl = (ap.name || '').toLowerCase();
    const isPonyRides = nl.includes('pony ride');
    const ponyLocked = isPonyRides && !hasAnimalParkTicketForPonyRides;
    if (isSoldOut || ponyLocked) return;

    setSelectedUpsell(ap);
    setModalOpen(true);
  }, [
    openAnimalParkUpsellNonce,
    loading,
    filteredUpsells,
    hasAnimalParkTicketForPonyRides
  ]);

  // Parent (Circus skip reminder "Stay — add Circus") bumps nonce → open Circus upsell modal
  useEffect(() => {
    if (!openCircusUpsellNonce || loading) return;
    if (openCircusUpsellNonce === lastHandledCircusNonceRef.current) return;

    const circus = filteredUpsells.find(u => ticketNameQualifiesForCircusBundleDiscount(u.name));
    if (!circus) return;

    lastHandledCircusNonceRef.current = openCircusUpsellNonce;

    const isSoldOut = circus.quantityAvailable === 0 || circus.isSoldOut === true;
    if (isSoldOut) return;

    setSelectedUpsell(circus);
    setModalOpen(true);
  }, [openCircusUpsellNonce, loading, filteredUpsells]);

  const handleChangeDate = (e) => {
    e.stopPropagation(); // Prevent card click
    if (onChangeDateClick) {
      onChangeDateClick();
    }
  };

  // Helper function to check if an upsell is completely sold out
  const isUpsellSoldOut = (upsell) => {
    // Check if quantityAvailable is 0 or if explicitly marked as sold out
    return upsell.quantityAvailable === 0 || upsell.isSoldOut === true;
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUpsell(null);
  };

  const handleUpsellItemAdd = (items, formattedQuestions = []) => {


    if (onUpsellItemAdd) {
      onUpsellItemAdd(items, formattedQuestions);
    }
  };

  if (loading) {
    return (
      <UpsellContainer>
        <LoadingSpinner>Loading experiences…</LoadingSpinner>
      </UpsellContainer>
    );
  }

  // If no upsells returned
  if (filteredUpsells.length === 0) {
    return (
      <UpsellContainer>
        <NoUpsells>
          <p>No additional experiences are available for your selected date and time.</p>
          <small>You can proceed with your booking as planned.</small>
        </NoUpsells>
      </UpsellContainer>
    );
  }

  return (
    <UpsellContainer>
      <UpsellIntroPanel>
        <UpsellIntroAccent />
        <SectionEyebrow>Enhance your visit</SectionEyebrow>
        <SectionTitle>Additional experiences</SectionTitle>
        <SectionSubtitle>
          Select any add-ons you'd like to include. Open a card for full details and pricing.
        </SectionSubtitle>
      </UpsellIntroPanel>

      {/* Featured tour pages: mandatory upsell notice */}
      {isFeaturedTour && tourConfig && (
        <RequiredBanner>
          <span>🏰</span>
          <div>
            <div>{tourConfig.displayName} is required for all visitors.</div>
            <div style={{ fontWeight: 400, fontSize: '0.875rem', marginTop: '0.2rem', opacity: 0.9 }}>
              Please add {totalEntryTickets} {totalEntryTickets === 1 ? 'ticket' : 'tickets'} — one for each person in your party — before continuing.
            </div>
          </div>
        </RequiredBanner>
      )}

      {requireAfternoonTea && (
        <RequiredBanner>
          <span>🫖</span>
          <div>
            <div>Festive Afternoon Tea is not included with Castle entry.</div>
            <div style={{ fontWeight: 400, fontSize: '0.875rem', marginTop: '0.2rem', opacity: 0.9 }}>
              Please add afternoon tea below to continue. You can add up to {totalEntryTickets} {totalEntryTickets === 1 ? 'tea' : 'teas'} — one per entry ticket.
            </div>
          </div>
        </RequiredBanner>
      )}

      {/* Circus journeys: explain Castle & Gardens bundle offers before users open cards */}
      {hasCircusTickets && filteredUpsells.length > 0 && (
        <CircusBundleOffersBanner role="region" aria-label="Circus bundle offers">
          <span className="banner-icon" aria-hidden>🎪</span>
          <div>
            {!hasCastleAndGardenEntryInBasket ? (
              <>
                <span className="banner-title">Add Castle and Garden Entry — two ways to save</span>
                <ul>
                  <li>
                    <strong>20% off entry tickets</strong> — Choose <strong>Castle and Garden Entry</strong> below with your Circus booking;
                    your Castle &amp; Gardens entry tickets receive 20% off at checkout.
                  </li>
                  <li>
                    <strong>Free Animal Park</strong> — Add <strong>Castle and Garden Entry</strong> here first, then open the{' '}
                    <strong>Animal Park</strong> card for the exclusive free Animal Park offer.
                  </li>
                </ul>
              </>
            ) : paidCastleQtyForFreeAnimalParkPromo > 0 ? (
              <div style={{ fontWeight: 600 }}>
                Castle &amp; Gardens entry added — 20% off those tickets applies at checkout. Use the{' '}
                <strong>Animal Park</strong> card below for <strong>free Animal Park</strong> entry.
              </div>
            ) : (
              <div style={{ fontWeight: 600 }}>
                Castle &amp; Gardens entry added — 20% off those tickets applies at checkout.{' '}
                <strong>Historic House Member</strong> tickets do not include free Animal Park — use the{' '}
                <strong>Animal Park</strong> card below to purchase Animal Park entry if required.
              </div>
            )}
          </div>
        </CircusBundleOffersBanner>
      )}

      {/* Only show when user has Circus AND has added an entry ticket (discount actually applied) */}
      {hasEligibleEntryForCircusDiscount && (
        <CircusDiscountActiveBanner role="status">
          <span aria-hidden>✓</span>
          <span>20% off entry tickets is active at checkout for qualifying tickets.</span>
        </CircusDiscountActiveBanner>
      )}

      <UpsellGrid>
        {filteredUpsells.map((upsell) => {
          const isSelected = (localSelections[upsell.id] || 0) > 0;
          const selectedQuantity = localSelections[upsell.id] || 0;
          const isSoldOut = isUpsellSoldOut(upsell);
          const isExclusiveAnimalPark =
            exclusiveAnimalParkFreeUpsellsOnly &&
            upsell.name &&
            String(upsell.name).toLowerCase().includes('animal park');
          const isPirateAdventureTrail = upsell.name && String(upsell.name).toLowerCase().includes('pirate adventure trail');
          const isThisCrownTourUpsell = isFeaturedTour && isFeaturedTourUpsell(upsell.name);
          const isThisRequiredAfternoonTea = requireAfternoonTea && isAfternoonTeaRequiredUpsellName(upsell.name);
          // Circus upsells that trigger 20% off entry — match Venpos naming (exclude workshops)
          const nameLower = upsell.name ? String(upsell.name).toLowerCase() : '';
          const isCircusUpsell = !!nameLower && ticketNameQualifiesForCircusBundleDiscount(upsell.name);
          const isEarlyBirdCircus = isCircusUpsell; // keep for CardWrapper
          const isPirateCircusCastleGarden =
            pageId === PIRATE_CIRCUS_PAGE_ID && ticketLineIsCastleAndGardenEntry(upsell.name);
          const isPirateCircusGardenPlayground =
            pageId === PIRATE_CIRCUS_PAGE_ID && isGardenPlaygroundUpsellCardName(upsell.name);
          const isPirateThemedUpsellCard =
            isPirateCircusCastleGarden || isPirateCircusGardenPlayground;

          const isCastleGardenCardName = isCastleGardenUpsellCardName(upsell.name);
          const isGardenPlaygroundCardName = isGardenPlaygroundUpsellCardName(upsell.name);
          const gardenPlaygroundLockedByCastle =
            hasCastleAndGardenEntryInBasket && isGardenPlaygroundCardName;
          const castleGardenLockedByGardenPlay =
            hasGardenPlaygroundOnlyEntryInBasket && isCastleGardenCardName;
          const isCastleGardenPlaygroundLocked =
            gardenPlaygroundLockedByCastle || castleGardenLockedByGardenPlay;
          const displayName = isExclusiveAnimalPark
            ? 'Exclusive Offer: Free Animal Park Entry'
            : isPirateCircusCastleGarden
              ? 'Castle & Gardens — all day adventure'
              : isPirateCircusGardenPlayground
                ? 'Garden & Playground — Pirate Takeover'
                : isHalloweenCraftsUpsellName(upsell.name)
                  ? HALLOWEEN_CRAFTS_DISPLAY_NAME
                  : upsell.name;
          const isAviaryFathersDayCard =
            !!visitDate &&
            isJune212026VisitDate(visitDate) &&
            isAviaryAfternoonTeaUpsellName(upsell.name);
          const isCastleGrottoCard = isSantasGrottoCastleUpsellName(upsell.name);
          const isWellnessFreeUpsell = isWellnessDiscoveryFreeUpsell(upsell.name);
          // Exclusive: Animal Park / Early Bird Circus; gold highlight: Father's Day Afternoon Tea (21 June 2026)
          const isMerchandise = upsell.pageType === 'Merchandise';
          const CardWrapper =
            isMerchandise
              ? UpsellCardExplorer
              : isWellnessFreeUpsell
                ? UpsellCardWellness
                : isPirateThemedUpsellCard
                  ? UpsellCardPirateCastle
                  : isExclusiveAnimalPark || isEarlyBirdCircus
                    ? UpsellCardExclusive
                    : isAviaryFathersDayCard
                      ? UpsellCardFathersDay
                      : UpsellCard;
          // Pony Rides: locked until an Animal Park ticket is in the basket (PONY_RIDES_UNLOCK_CODES)
          const isPonyRides = nameLower.includes('pony ride');
          const isPonyRidesLocked = isPonyRides && !hasAnimalParkTicketForPonyRides;
          const isUpsellInteractionLocked = isPonyRidesLocked || isCastleGardenPlaygroundLocked;

          return (
            <CardWrapper 
              key={upsell.id} 
              $isSelected={isSelected}
              isSoldOut={isSoldOut}
              $isLocked={isUpsellInteractionLocked}
              onClick={isMerchandise ? undefined : () => handleUpsellClick(upsell, isSoldOut, isUpsellInteractionLocked)}
              style={{ cursor: isMerchandise ? 'default' : (isSoldOut || isUpsellInteractionLocked) ? 'not-allowed' : 'pointer' }}
            >
              {isSoldOut && <SoldOutBadge>Sold Out</SoldOutBadge>}
              {!isSoldOut && isPonyRidesLocked && <LockedBadge>🔒 Locked</LockedBadge>}
              {!isSoldOut && !isPonyRidesLocked && isCastleGardenPlaygroundLocked && (
                <LockedBadge>🔒 Not with your tickets</LockedBadge>
              )}
              {(isThisCrownTourUpsell || isThisRequiredAfternoonTea) && !isSoldOut && <RequiredBadge>Required</RequiredBadge>}
              {!isSoldOut && isSelected && <SelectedBadge>{selectedQuantity}</SelectedBadge>}
              
              <UpsellImageWrap>
                {isMerchandise && (
                  <ExplorerImageStripe>Book online &amp; save</ExplorerImageStripe>
                )}
                {isPirateCircusCastleGarden && (
                  <PirateCastleImageStripe>Pirate Takeover · Full day added value</PirateCastleImageStripe>
                )}
                {isPirateCircusGardenPlayground && (
                  <PirateCastleImageStripe>Pirate Takeover · Includes Pirate Trail</PirateCastleImageStripe>
                )}
                {isAviaryFathersDayCard && (
                  <FathersDayImageStripe>Father&apos;s Day Special</FathersDayImageStripe>
                )}
                {isWellnessFreeUpsell && (
                  <WellnessImageStripe>Free · Belvoir Wellness</WellnessImageStripe>
                )}
                <UpsellImage
                  imageUrl={upsell.imageUrl}
                  $bgPosition={isPonyRides ? 'center top -60px' : undefined}
                />
              </UpsellImageWrap>
              
              <UpsellContent>
                <div>
                  <UpsellName>{displayName}</UpsellName>
                  {isWellnessFreeUpsell && (
                    <UpsellSubtitle
                      style={{
                        background: 'linear-gradient(135deg, #efe6dc 0%, #d7b7a7 100%)',
                        color: '#302d2c',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '8px',
                        fontWeight: '700',
                        marginBottom: '0.65rem',
                        fontSize: '0.85rem',
                        border: '1px solid rgba(178, 156, 141, 0.45)',
                      }}
                    >
                      Free to add — no extra charge
                    </UpsellSubtitle>
                  )}
                  {isCastleGrottoCard && (
                    <UpsellSubtitle>Children’s tickets only — Castle entry ticket required</UpsellSubtitle>
                  )}
                  {isMerchandise && (
                    <ExplorerPromoCopy>
                      Book online and save — cheapest meals online. Pre-order now and skip the queues.
                    </ExplorerPromoCopy>
                  )}
                  {isPirateCircusCastleGarden && (
                    <UpsellSubtitle
                      style={{
                        background: 'linear-gradient(135deg, #3d2914 0%, #5c3d1e 100%)',
                        color: '#fdf6e3',
                        padding: '0.55rem 0.85rem',
                        borderRadius: '8px',
                        fontWeight: '600',
                        marginBottom: '0.65rem',
                        fontSize: '0.88rem',
                        lineHeight: 1.5,
                        border: '1px solid rgba(212, 175, 55, 0.45)',
                      }}
                    >
                      Enjoy an all day adventure — explore the castle &amp; gardens alongside your Circus visit, and the{' '}
                      <strong style={{ fontWeight: 800 }}>Pirate Adventure Trail</strong> in the Adventure Playground.
                      Brilliant added value: bundle with BOOM Circus for <strong style={{ fontWeight: 800 }}>20% off</strong>{' '}
                      Castle &amp; Gardens entry, then add <strong style={{ fontWeight: 800 }}>free Animal Park</strong> from
                      the Animal Park card.
                    </UpsellSubtitle>
                  )}
                  {isPirateCircusGardenPlayground && (
                    <UpsellSubtitle
                      style={{
                        background: 'linear-gradient(135deg, #3d2914 0%, #5c3d1e 100%)',
                        color: '#fdf6e3',
                        padding: '0.55rem 0.85rem',
                        borderRadius: '8px',
                        fontWeight: '600',
                        marginBottom: '0.65rem',
                        fontSize: '0.88rem',
                        lineHeight: 1.5,
                        border: '1px solid rgba(212, 175, 55, 0.45)',
                      }}
                    >
                      This entry includes the <strong style={{ fontWeight: 800 }}>Pirate Adventure Trail</strong> in the
                      Adventure Playground.
                    </UpsellSubtitle>
                  )}
                  {isAviaryFathersDayCard && (
                    <UpsellSubtitle
                      style={{
                        background: 'linear-gradient(135deg, #fdf6e3 0%, #f4e4bc 100%)',
                        color: '#1a2a4a',
                        padding: '0.55rem 0.85rem',
                        borderRadius: '8px',
                        fontWeight: '700',
                        marginBottom: '0.65rem',
                        fontSize: '0.88rem',
                        lineHeight: 1.45,
                        border: '1px solid rgba(201, 162, 39, 0.45)',
                      }}
                    >
                      Father&apos;s Day Special — Gentleman&apos;s Afternoon Tea available (click below to choose options)
                    </UpsellSubtitle>
                  )}
                  {isPirateAdventureTrail && !hasCircusTickets && <UpsellSubtitle>Including Gardens</UpsellSubtitle>}
                  {/* When user has Circus: Pirate Trail upsells get 20% off — show add for 20% off */}
                  {isPirateAdventureTrail && hasCircusTickets && (
                    <UpsellSubtitle style={{
                      background: 'linear-gradient(135deg, #f4d03f 0%, #d4af37 100%)',
                      color: '#1a3a52',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      fontWeight: '700',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem'
                    }}>
                      🎪 Add for 20% off — 20% off when you have a Circus ticket
                    </UpsellSubtitle>
                  )}
                  {/* Circus card only: when not in basket, show add for 20% off entry tickets */}
                  {isCircusUpsell && !hasCircusTickets && (
                    <UpsellSubtitle style={{ 
                      background: 'linear-gradient(135deg, #f4d03f 0%, #d4af37 100%)', 
                      color: '#1a3a52', 
                      padding: '0.5rem 0.75rem', 
                      borderRadius: '6px', 
                      fontWeight: '700',
                      marginBottom: '0.75rem',
                      fontSize: '0.85rem'
                    }}>
                      🎪 Add for 20% off your Entry tickets
                    </UpsellSubtitle>
                  )}
                  
                  {upsell.description && !isPirateThemedUpsellCard && !isMerchandise && (
                    <UpsellDescription>{upsell.description}</UpsellDescription>
                  )}
                </div>

                <UpsellCardFooter>
                  {isSoldOut ? (
                    <>
                      <UpsellCardStatusNote $variant="danger">Fully booked for this date</UpsellCardStatusNote>
                      <ChangeDateLink onClick={handleChangeDate}>Change date</ChangeDateLink>
                    </>
                  ) : isPonyRidesLocked ? (
                    <UpsellCardStatusNote>Add an Animal Park ticket to unlock Pony Rides</UpsellCardStatusNote>
                  ) : isCastleGardenPlaygroundLocked ? (
                    <UpsellCardStatusNote>
                      {gardenPlaygroundLockedByCastle
                        ? 'Included in Castle & Gardens entry'
                        : 'Remove Garden & Playground to switch'}
                    </UpsellCardStatusNote>
                  ) : isMerchandise ? (
                    <>
                      {upsell.price > 0 && (
                        <UpsellPrice>£{upsell.price.toFixed(2)}<UpsellPricePer>each</UpsellPricePer></UpsellPrice>
                      )}
                      {isSelected ? (
                        <MerchandiseQtyRow onClick={e => e.stopPropagation()}>
                          <MerchandiseQtyBtn onClick={e => handleMerchandiseDecrement(e, upsell)} aria-label="Remove one">−</MerchandiseQtyBtn>
                          <MerchandiseQtyCount>{selectedQuantity}</MerchandiseQtyCount>
                          <MerchandiseQtyBtn onClick={e => handleMerchandiseAdd(e, upsell)} aria-label="Add one">+</MerchandiseQtyBtn>
                        </MerchandiseQtyRow>
                      ) : (
                        <MerchandiseAddBtnStrong onClick={e => handleMerchandiseAdd(e, upsell)}>
                          Book online &amp; save
                        </MerchandiseAddBtnStrong>
                      )}
                    </>
                  ) : (
                    <>
                      {isWellnessFreeUpsell ? (
                        <WellnessFreeTag>Free</WellnessFreeTag>
                      ) : upsell.price > 0 ? (
                        <UpsellPrice>£{upsell.price.toFixed(2)}<UpsellPricePer>pp</UpsellPricePer></UpsellPrice>
                      ) : isExclusiveAnimalPark ? (
                        <UpsellFreeTag>Free</UpsellFreeTag>
                      ) : (
                        <span />
                      )}
                      <UpsellCardAction $selected={isSelected}>
                        {isSelected
                          ? (isThisCrownTourUpsell
                              ? (selectedQuantity >= totalEntryTickets
                                  ? `✓ ${selectedQuantity} of ${totalEntryTickets}`
                                  : `${selectedQuantity} of ${totalEntryTickets} — add more`)
                              : isThisRequiredAfternoonTea
                                ? `✓ ${selectedQuantity} of ${totalEntryTickets} max`
                              : `✓ ${selectedQuantity} added`)
                          : isExclusiveAnimalPark || isWellnessFreeUpsell
                            ? 'Add free →'
                            : 'View →'}
                      </UpsellCardAction>
                    </>
                  )}
                </UpsellCardFooter>
                {(isThisCrownTourUpsell || isThisRequiredAfternoonTea) && totalEntryTickets > 0 && (
                  <MaxQtyNote>
                    {isThisRequiredAfternoonTea
                      ? `Max ${totalEntryTickets} — cannot exceed your entry tickets`
                      : `Max ${totalEntryTickets} — one per entry ticket`}
                  </MaxQtyNote>
                )}
              </UpsellContent>
            </CardWrapper>
          );
        })}
      </UpsellGrid>

      <UpsellDetailModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        upsell={selectedUpsell}
        selectedDate={selectedDate}
        selectedTimeSlot={selectedTimeSlot}
        selectedTickets={selectedTickets}
        locationId={locationId}
        pageId={pageId}
        requireAfternoonTea={requireAfternoonTea}
        onAddToBasket={handleUpsellItemAdd}
        onRemoveUpsell={(upsellId) => {
          if (onUpsellRemove) onUpsellRemove(upsellId);
        }}
        onChangeDateClick={onChangeDateClick}
      />
    </UpsellContainer>
  );
};

export default UpsellStep;